/**
 * API Client Service
 * Handles HTTP requests to the backend API
 */

import * as Sentry from '@sentry/react';
import { getCurrentUserToken } from './auth';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5206';
const ACTIVE_ORG_STORAGE_KEY = 'activeOrganizationId';

/**
 * Get the active organization ID for system admin org switching
 */
const getOrganizationOverrideId = (): string | null => {
  return localStorage.getItem(ACTIVE_ORG_STORAGE_KEY);
};

export class ApiError extends Error {
  statusCode: number;
  response?: unknown;

  constructor(message: string, statusCode: number, response?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.statusCode = statusCode;
    this.response = response;
  }
}

export interface ApiRequestOptions extends RequestInit {
  timeout?: number;
  responseType?: 'json' | 'blob';
}

/**
 * The error code the API returns (with a 403) for every request made by a user
 * whose organization has been paused.
 */
const ORGANIZATION_PAUSED_CODE = 'organization_paused';

/**
 * The error code the API returns (with a 403) for every non-allowlisted request
 * made by a user whose self-serve organization has not paid yet. Its own code,
 * not the pause: the remedy is "complete payment", not "contact support".
 */
const ORGANIZATION_AWAITING_PAYMENT_CODE = 'organization_awaiting_payment';

/**
 * Notified with the server-provided pause message (or null if the server sent
 * none) whenever a request is rejected because the organization is paused.
 */
export type OrganizationPausedHandler = (message: string | null) => void;

/**
 * Notified with the server's copy (or null) whenever a request is rejected
 * because the organization is awaiting its first payment.
 */
export type OrganizationAwaitingPaymentHandler = (message: string | null) => void;

const organizationPausedHandlers = new Set<OrganizationPausedHandler>();
const organizationAwaitingPaymentHandlers = new Set<OrganizationAwaitingPaymentHandler>();

/**
 * Subscribe to mid-session organization pauses. Returns an unsubscribe function.
 *
 * This lets AuthContext react to an organization being paused while the user is
 * already logged in (their cached profile still says "not paused").
 */
export const subscribeToOrganizationPaused = (
  handler: OrganizationPausedHandler
): (() => void) => {
  organizationPausedHandlers.add(handler);
  return () => {
    organizationPausedHandlers.delete(handler);
  };
};

/**
 * Subscribe to "organization awaiting payment" refusals. Returns an unsubscribe
 * function. AuthContext uses it to route the user to /signup/payment instead of
 * leaving them in an app where every request fails.
 */
export const subscribeToOrganizationAwaitingPayment = (
  handler: OrganizationAwaitingPaymentHandler
): (() => void) => {
  organizationAwaitingPaymentHandlers.add(handler);
  return () => {
    organizationAwaitingPaymentHandlers.delete(handler);
  };
};

type BlockingState = 'paused' | 'awaitingPayment';

/**
 * Notify subscribers if this failed response is one of the organization-level
 * 403s (paused, or awaiting payment). Never throws - the caller still throws its
 * own ApiError afterwards.
 *
 * Returns which state it was, so the caller can log it as the known, handled
 * state it is rather than as an unexpected API error.
 */
const notifyIfOrganizationBlocked = (statusCode: number, body: unknown): BlockingState | null => {
  if (statusCode !== 403 || typeof body !== 'object' || body === null) {
    return null;
  }

  const { code, message } = body as { code?: unknown; message?: unknown };
  let state: BlockingState;
  let handlers: Set<(message: string | null) => void>;

  if (code === ORGANIZATION_PAUSED_CODE) {
    state = 'paused';
    handlers = organizationPausedHandlers;
  } else if (code === ORGANIZATION_AWAITING_PAYMENT_CODE) {
    state = 'awaitingPayment';
    handlers = organizationAwaitingPaymentHandlers;
  } else {
    return null;
  }

  const serverMessage = typeof message === 'string' && message.trim() ? message : null;

  handlers.forEach((handler) => {
    try {
      handler(serverMessage);
    } catch (handlerError) {
      console.error(`Organization ${state} handler failed:`, handlerError);
    }
  });

  return state;
};

/**
 * Make an authenticated API request
 */
export const apiRequest = async <T>(
  endpoint: string,
  options: ApiRequestOptions = {}
): Promise<T> => {
  // Get fresh token from Firebase (automatically refreshes if expired)
  const token = await getCurrentUserToken();
  const { timeout, responseType = 'json', ...fetchOptions } = options;

  // Safely extract custom headers
  let customHeaders: Record<string, string> = {};
  if (options.headers) {
    if (options.headers instanceof Headers) {
      // Convert Headers object to plain object
      options.headers.forEach((value, key) => {
        customHeaders[key] = value;
      });
    } else if (Array.isArray(options.headers)) {
      // Convert array of tuples to object
      options.headers.forEach(([key, value]) => {
        customHeaders[key] = value;
      });
    } else if (typeof options.headers === 'object') {
      // Already a plain object
      customHeaders = options.headers as Record<string, string>;
    }
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...customHeaders,
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Add organization override header for system admin org switching
  const orgOverrideId = getOrganizationOverrideId();
  if (orgOverrideId) {
    headers['X-Organization-Id'] = orgOverrideId;
  }

  const url = `${API_BASE_URL}${endpoint}`;

  // Create abort controller for timeout
  const controller = new AbortController();
  const timeoutId = timeout ? setTimeout(() => controller.abort(), timeout) : null;

  // If caller provided their own signal (e.g. for cancellation), link it to our controller
  const callerSignal = fetchOptions.signal;
  if (callerSignal) {
    delete fetchOptions.signal;
    if (callerSignal.aborted) {
      controller.abort();
    } else {
      callerSignal.addEventListener('abort', () => controller.abort(), { once: true });
    }
  }

  try {
    const response = await fetch(url, {
      ...fetchOptions,
      headers,
      signal: controller.signal,
    });

    if (timeoutId) clearTimeout(timeoutId);

    if (!response.ok) {
      let errorMessage = `API request failed: ${response.status} ${response.statusText}`;
      let errorData: unknown;

      try {
        errorData = await response.json();
        const parsedMessage = (errorData as { message?: unknown }).message;
        if (typeof parsedMessage === 'string' && parsedMessage) {
          errorMessage = parsedMessage;
        }
      } catch {
        // Response is not JSON, use default message
      }

      // Let subscribers (AuthContext) know the org was paused, or is awaiting
      // payment. Additive only - the ApiError below is still thrown as before.
      const blockingState = notifyIfOrganizationBlocked(response.status, errorData);

      const method = fetchOptions.method || 'GET';
      // Query strings are stripped before anything is reported: they carry
      // search terms, which are PII in this app.
      const path = endpoint.split('?')[0];

      if (blockingState) {
        // A paused or unpaid org is a known, handled state, and between the
        // refusal and the blocking screen *every* in-flight request 403s - one
        // concise breadcrumb each instead of a full error context with the body.
        Sentry.addBreadcrumb({
          category: 'auth',
          level: 'info',
          message:
            blockingState === 'paused'
              ? '403 organization_paused - routing to blocking screen'
              : '403 organization_awaiting_payment - routing to payment screen',
          data: { path, method },
        });
      } else {
        // Add Sentry context for API errors
        Sentry.setContext('api_request', {
          endpoint: path,
          method,
          status_code: response.status,
          error_data: errorData,
        });
      }

      throw new ApiError(errorMessage, response.status, errorData);
    }

    // Handle empty responses (204 No Content)
    if (response.status === 204) {
      return {} as T;
    }

    // Parse response based on type
    if (responseType === 'blob') {
      return await response.blob() as T;
    }

    return await response.json();
  } catch (error) {
    if (timeoutId) clearTimeout(timeoutId);

    // Handle timeout
    if (error instanceof Error && error.name === 'AbortError') {
      Sentry.addBreadcrumb({
        category: 'api_timeout',
        message: `Request aborted: ${fetchOptions.method || 'GET'} ${endpoint}`,
        level: 'warning',
        data: {
          endpoint,
          method: fetchOptions.method || 'GET',
          had_timeout: !!timeout,
          timeout_ms: timeout,
        },
      });
      throw new ApiError('Request timeout', 408);
    }

    if (error instanceof ApiError) {
      throw error;
    }

    // Network or other errors
    throw new ApiError(
      error instanceof Error ? error.message : 'Network request failed',
      0
    );
  }
};

/**
 * GET request
 */
export const get = <T>(endpoint: string, options?: ApiRequestOptions): Promise<T> => {
  return apiRequest<T>(endpoint, { ...options, method: 'GET' });
};

/**
 * POST request
 */
export const post = <T>(
  endpoint: string,
  data?: unknown,
  options?: ApiRequestOptions
): Promise<T> => {
  return apiRequest<T>(endpoint, {
    ...options,
    method: 'POST',
    body: data ? JSON.stringify(data) : undefined,
  });
};

/**
 * PUT request
 */
export const put = <T>(
  endpoint: string,
  data?: unknown,
  options?: ApiRequestOptions
): Promise<T> => {
  return apiRequest<T>(endpoint, {
    ...options,
    method: 'PUT',
    body: data ? JSON.stringify(data) : undefined,
  });
};

/**
 * PATCH request
 */
export const patch = <T>(
  endpoint: string,
  data?: unknown,
  options?: ApiRequestOptions
): Promise<T> => {
  return apiRequest<T>(endpoint, {
    ...options,
    method: 'PATCH',
    body: data ? JSON.stringify(data) : undefined,
  });
};

/**
 * DELETE request
 */
export const del = <T>(endpoint: string, options?: ApiRequestOptions): Promise<T> => {
  return apiRequest<T>(endpoint, { ...options, method: 'DELETE' });
};

/**
 * GET request that returns a blob (for file downloads)
 */
export const getBlob = (endpoint: string, options?: ApiRequestOptions): Promise<Blob> => {
  return apiRequest<Blob>(endpoint, {
    ...options,
    method: 'GET',
    responseType: 'blob'
  });
};
